import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import { OverlayLoader, NeoCard, NeoInput, NeoButton, NeoBadge } from "../../../../components/Common";
import { PERMISSION_GROUPS } from "../constants";
import { roleDefaults } from "../../../../models/role";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { METRIC_GROUP_OPTIONS, METRIC_GROUP_DESCRIPTIONS } from "../../../../constants/performance";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";

/**
 * PermissionEditor
 * Editor que coloca tudo em um único Card, sem blocos flutuantes, compacto e direto.
 * Migrado para o Neo Design System.
 */
const PermissionEditor = ({ role, onSave, onDelete, isSaving, onChange }) => {
    const [formData, setFormData] = useState(roleDefaults);

    useEffect(() => {
        if (role) {
            setFormData({
                id: role.id || null,
                name: role.name || "",
                description: role.description || "",
                status: role.status || GLOBAL_STATUS.ACTIVE,
                permissions: role.permissions || {},
                metricGroup: role.metricGroup || "",
            });
        } else {
            setFormData(roleDefaults);
        }
    }, [role]);

    const handleInputChange = (e) => {
        const { name, value, checked } = e.target;
        if (onChange) onChange(true);
        if (name === "statusSwitch") {
            setFormData(prev => ({ ...prev, status: checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE }));
        } else {
            setFormData(prev => ({ ...prev, [name || e.target.id]: value }));
        }
    };

    const togglePermission = (permissionId) => {
        if (onChange) onChange(true);
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [permissionId]: !prev.permissions[permissionId]
            }
        }));
    };

    const toggleGroup = (groupPermissions, isAllActive) => {
        if (onChange) onChange(true);
        const newPermissions = { ...formData.permissions };
        groupPermissions.forEach(perm => {
            newPermissions[perm.id] = !isAllActive;
        });
        setFormData(prev => ({ ...prev, permissions: newPermissions }));
    };

    const handleDuplicate = () => {
        setFormData(prev => ({
            ...prev,
            id: null,
            name: `${prev.name} (Cópia)`
        }));
        if (onChange) onChange(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    if (!role && !formData.name) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center animate-fadeIn" style={{ minHeight: "60vh" }}>
                <div className="avatar-xl mb-4">
                    <span className="avatar-title bg-soft-primary text-primary rounded-circle shadow-sm" style={{ fontSize: "48px" }}>
                        <i className="mdi mdi-shield-account-outline"></i>
                    </span>
                </div>
                <h4 className="fw-semibold text-dark">Gerenciamento de Cargos</h4>
                <p className="text-muted w-50">Selecione um cargo na lista lateral para visualizar suas permissões ou clique em <strong>"Novo"</strong> para criar um cargo.</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="fade-in pb-4 h-100">
            <NeoCard
                title={
                    <span className="d-flex align-items-center gap-2">
                        <span className="text-truncate">{formData.id ? formData.name : formData.name || "Novo Cargo"}</span>
                        {formData.id && (
                            <NeoBadge color={formData.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                                {GLOBAL_STATUS_LABELS[formData.status] || 'Desconhecido'}
                            </NeoBadge>
                        )}
                    </span>
                }
                subtitle="Gerenciar permissões do perfil"
                headerIcon="mdi-shield-check-outline"
                headerActions={
                    <div className="d-flex gap-2 flex-shrink-0">
                        {formData.id && (
                            <>
                                <NeoButton
                                    type="button"
                                    variant="outline-primary"
                                    square={true}
                                    onClick={handleDuplicate}
                                    title="Duplicar"
                                    icon="mdi-content-copy"
                                />
                                <NeoButton
                                    type="button"
                                    variant="outline-danger"
                                    square={true}
                                    onClick={() => onDelete(formData.id)}
                                    title="Excluir"
                                    icon="mdi-trash-can-outline"
                                />
                            </>
                        )}
                        <NeoButton
                            type="submit"
                            variant="primary"
                            disabled={isSaving || !formData.name}
                        >
                            {isSaving ? "Salvando" : "Salvar"}
                        </NeoButton>
                    </div>
                }
                className="overflow-hidden rounded-0 position-relative animate__animated animate__fadeIn"
                bodyClassName="p-4"
                noBorder={true}
            >
                <OverlayLoader show={isSaving} label="Salvando cargo..." />

                {/* INFORMAÇÕES BÁSICAS */}
                <div className="mb-4 pb-4 border-bottom" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                    <div className="d-flex align-items-center mb-3">
                        <h6 className="mb-0 fw-semibold text-dark flex-grow-1">Informações do Perfil</h6>
                        <div className="form-check form-switch mb-0" dir="ltr">
                            <input
                                type="checkbox"
                                role="switch"
                                name="statusSwitch"
                                id="statusSwitch"
                                className="form-check-input mt-1 cursor-pointer"
                                checked={formData.status === GLOBAL_STATUS.ACTIVE}
                                onChange={handleInputChange}
                            />
                            <label className="form-check-label mb-0 fw-medium cursor-pointer small ms-2" htmlFor="statusSwitch">
                                {GLOBAL_STATUS_LABELS[formData.status] || formData.status}
                            </label>
                        </div>
                    </div>

                    <Row>
                        <Col lg={4} md={6} className="mb-3 mb-md-0">
                            <NeoInput
                                label="Nome do Cargo *"
                                name="name"
                                id="name"
                                placeholder="Ex: Treinador, Recepcionista"
                                value={formData.name}
                                onChange={handleInputChange}
                                flat={true}
                                required
                            />
                        </Col>
                        <Col lg={8} md={6} className="mb-0">
                            <NeoInput
                                label="Breve Descrição"
                                name="description"
                                id="description"
                                placeholder="Qual a principal função deste papel no sistema?"
                                value={formData.description}
                                onChange={handleInputChange}
                                flat={true}
                            />
                        </Col>
                    </Row>
                </div>

                {/* GRUPO DE MÉTRICAS */}
                <div className="mb-4 pb-4 border-bottom" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                    <div className="d-flex align-items-center mb-3">
                        <h6 className="mb-0 fw-semibold text-dark flex-grow-1">
                            <i className="mdi mdi-chart-bar me-1 text-primary"></i>
                            Performance & Ranking
                        </h6>
                    </div>
                    <Row>
                        <Col lg={4} md={6} className="mb-2">
                            <NeoInput
                                label="Grupo de Métricas"
                                name="metricGroup"
                                id="metricGroup"
                                type="select"
                                value={formData.metricGroup}
                                onChange={handleInputChange}
                                flat={true}
                            >
                                <option value="">Nenhum (não participa do ranking)</option>
                                {METRIC_GROUP_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </NeoInput>
                        </Col>
                        <Col lg={8} md={6} className="d-flex align-items-end">
                            <p className="text-muted small mb-2">
                                <i className="mdi mdi-information-outline me-1"></i>
                                {formData.metricGroup
                                    ? METRIC_GROUP_DESCRIPTIONS[formData.metricGroup] || "Métricas aplicáveis a este grupo."
                                    : "Colaboradores com este cargo não serão avaliados no módulo de Performance."
                                }
                            </p>
                        </Col>
                    </Row>
                </div>

                {/* MATRIZ DE PERMISSÕES */}
                <h6 className="mb-3 fw-semibold text-dark">Matriz de Permissões</h6>
                <div className="neo-permission-matrix overflow-hidden border rounded" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                    {PERMISSION_GROUPS.map((group, gIdx) => {
                        const activeCount = group.permissions.filter(p => formData.permissions[p.id]).length;
                        const totalCount = group.permissions.length;
                        const isAllActive = activeCount === totalCount && totalCount > 0;
                        const isLast = gIdx === PERMISSION_GROUPS.length - 1;

                        return (
                            <div key={gIdx} className={!isLast ? "border-bottom" : ""} style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                {/* Cabeçalho do Grupo */}
                                <div className="bg-light bg-opacity-75 py-2 px-3 d-flex justify-content-between align-items-center border-bottom" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                    <h6 className="mb-0 fw-semibold text-dark d-flex align-items-center font-size-13">
                                        <i className="mdi mdi-shield-key-outline text-primary me-2 font-size-16"></i>
                                        {group.name}
                                    </h6>
                                    <button
                                        type="button"
                                        className={`btn btn-sm shadow-none p-0 fw-medium ${isAllActive ? 'text-primary' : 'text-muted'}`}
                                        onClick={() => toggleGroup(group.permissions, isAllActive)}
                                        style={{ fontSize: "12px" }}
                                    >
                                        {isAllActive ?
                                            <><i className="mdi mdi-check-all me-1"></i> Desmarcar Todos</> :
                                            <><i className="mdi mdi-check-underline me-1"></i> Marcar Todos</>}
                                    </button>
                                </div>

                                {/* Grid de Permissões */}
                                <div className="p-3 bg-white">
                                    <Row>
                                        {group.permissions.map((perm) => (
                                            <Col md={6} lg={4} key={perm.id}>
                                                <div className="form-check form-switch py-2 ps-5 pe-3 rounded hover-bg-light transition-all mb-0" style={{ cursor: 'pointer', minHeight: '38px' }}>
                                                    <input
                                                        type="checkbox"
                                                        role="switch"
                                                        id={`perm-${perm.id}`}
                                                        checked={formData.permissions[perm.id] === true}
                                                        onChange={() => togglePermission(perm.id)}
                                                        className="form-check-input mt-2 cursor-pointer"
                                                    />
                                                    <label
                                                        className="form-check-label small mb-0 ms-1 cursor-pointer text-truncate d-block pt-1"
                                                        htmlFor={`perm-${perm.id}`}
                                                        title={perm.label}
                                                        style={{ marginTop: '1px' }}
                                                    >
                                                        {perm.label}
                                                    </label>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </NeoCard>
        </form>
    );
};

PermissionEditor.propTypes = {
    role: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default PermissionEditor;
